import { Button, CodeInput, FormField, Text } from "@aurem/ui";
import { FormEvent, MouseEvent, useCallback, useMemo, useState } from "react";
import { useTranslations } from "../i18n";

type UserAuthResponse = {
  accessToken?: string | null;
  refreshToken?: string | null;
  userId: string;
  isMfaRequired: boolean;
};

type CodeOnlyProps = {
  mode: "reauthentication";
  onSuccess: (code: string) => void;
  onClose: () => void;
  submit?: never;
  email?: string;
  password?: string;
};

type AuthModeProps = {
  mode?: "auth";
  email: string;
  password: string;
  submit: (args: {
    email: string;
    password: string;
    code: string;
    onError?: () => void;
  }) => Promise<UserAuthResponse>;
  onSuccess: (tokens: UserAuthResponse) => void;
  onClose: () => void;
};

type MfaCodeInputProps = (CodeOnlyProps | AuthModeProps) & {
  loading?: boolean;
  errorMessage?: string | null | undefined;
};

export const MfaCodeInputForm = ({
  email,
  password,
  onSuccess,
  submit,
  onClose,
  loading = false,
  mode,
  errorMessage,
}: MfaCodeInputProps) => {
  const [code, setCode] = useState<string>("");
  const { t } = useTranslations();

  const labels = useMemo(() => {
    if (mode === "reauthentication") {
      return {
        title: t("apps.employee.reauthentication.mfa.heading"),
        description: t("apps.employee.reauthentication.mfa.description"),
        submit: t("apps.admin.common.submit"),
        cancel: t("apps.admin.common.cancel"),
        errorMessage: t("apps.admin.pages.profile.page.mfa.invalidCode"),
      };
    }
    return {
      title: t("apps.admin.pages.signIn.page.signIn.mfa.heading"),
      description: t("apps.admin.pages.signIn.page.signIn.mfa.description"),
      submit: t("apps.admin.common.submit"),
      cancel: t("apps.admin.common.cancel"),
      errorMessage: t("apps.admin.pages.signIn.page.signIn.mfa.error"),
    };
  }, [mode, t]);

  const isValid = code.replace(/\s/g, "").length === 6;

  const clear = useCallback(() => {
    setCode("");
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const codeValue = code.replace(/\s/g, "");

    if (mode === "reauthentication") {
      onSuccess(codeValue);
      return;
    }
    try {
      const result = await submit?.({
        email: email ?? "",
        password: password ?? "",
        code: codeValue,
        onError: clear,
      });
      if (result) onSuccess(result);
    } catch {
      clear();
    }
  };

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-2">
        <Text className="text-neutral-500">{labels.title}</Text>
        <Text size="sm" className="text-neutral-400">
          {labels.description}
        </Text>
      </div>
      <FormField message={errorMessage ? labels.errorMessage : undefined}>
        <CodeInput
          value={code}
          onChange={setCode}
          invalid={!!errorMessage}
          length={6}
          inputType="numeric"
        />
      </FormField>

      <div className="flex flex-row justify-end gap-2">
        <Button
          variant="outlined"
          color="neutral"
          type="button"
          onClick={handleClose}
        >
          {labels.cancel}
        </Button>
        <Button disabled={loading || !isValid} type="submit">
          {labels.submit}
        </Button>
      </div>
    </form>
  );
};
